import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import BulkPortalInviteModal from "../components/BulkPortalInviteModal";
import PortalStatusBadge from "../components/PortalStatusBadge";
import CategoryManager from "../components/CategoryManager";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Loader2,
  Plus,
  Search,
  User,
  Phone,
  Mail,
  MapPin,
  Building2,
  Eye,
  Edit,
  Trash2,
  Tag,
  Filter,
  LayoutGrid,
  List,
  X,
  ChevronDown,
  UserPlus,
  Sparkles,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyClientForm = {
  // Basic Info (required)
  first_name: "", // Legal first name
  last_name: "", // Legal last name
  preferred_name: "",
  title: "", // Mr, Mrs, Ms, Dr, etc.
  nickname: "",
  
  // Contact
  phones: [{ type: "cell", number: "", extension: "", is_primary: true }],
  emails: [{ type: "personal", email: "", is_primary: true }],
  fax: "",
  addresses: [{ type: "primary", street: "", unit: "", city: "", province: "", postal_code: "", country: "Canada" }],
  
  // Company
  company_id: "",
  company_name: "",
  
  // Personal Info
  birthday: "",
  anniversary: "",
  preferred_contact_method: "email",
  preferred_contact_time: "",
  
  // Financial
  payment_terms: "Net 30",
  credit_limit: "",
  tax_exempt: false,
  
  // Banking (optional)
  banking_info: {
    bank_name: "",
    account_holder: "",
    account_number: "",
    transit_number: "",
    institution_number: ""
  },
  
  // Additional Info
  allergies: "",
  preferences: "",
  special_requirements: "",
  
  // Organization
  tags: [],
  category: "",
  
  // Sub Contacts
  sub_contacts: [],
  
  // Notes
  notes: "",
  
  // Custom fields for any additional info
  custom_fields: {},
  
  // Phase 19.23 — Portal access invitation
  grant_portal_access: false,
  send_invite_now: null,  // null = honor tenant default
};

const Clients = () => {
  const { isOfficeManager, isQuoter } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [viewMode, setViewMode] = useState("list"); // list or grid
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [tagInput, setTagInput] = useState("");
  const [stats, setStats] = useState({ total: 0, with_company: 0, independent: 0 });
  // Phase 19.23 — bulk portal invite
  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);
  const [autoSendInvitesDefault, setAutoSendInvitesDefault] = useState(false);

  const fetchClients = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterCompany && filterCompany !== "all") params.append("company_id", filterCompany);
      if (filterTag) params.append("tag", filterTag);
      
      const response = await axios.get(`${API}/clients?${params.toString()}`);
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  }, [search, filterCompany, filterTag]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/companies`);
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  const fetchInvitePreferences = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/portal/invite-preferences`);
      setAutoSendInvitesDefault(!!response.data?.auto_send_portal_invites);
    } catch (error) {
      // Not critical
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchClients(), fetchCompanies(), fetchStats(), fetchInvitePreferences()]);
      setLoading(false);
      
      // Check for quick action
      const quickAction = sessionStorage.getItem("quickAction");
      if (quickAction === "add-client") {
        sessionStorage.removeItem("quickAction");
        resetForm();
        setDialogOpen(true);
      }
    };
    loadData();
  }, [fetchClients, fetchCompanies, fetchStats, fetchInvitePreferences]);

  const resetForm = () => {
    setClientForm(emptyClientForm);
    setEditMode(false);
    setEditId(null);
  };

  const handleEdit = (client) => {
    setClientForm({
      first_name: client.first_name || "",
      last_name: client.last_name || "",
      preferred_name: client.preferred_name || "",
      title: client.title || "",
      nickname: client.nickname || "",
      phones: client.phones?.length > 0 ? client.phones : [{ type: "cell", number: "", extension: "", is_primary: true }],
      emails: client.emails?.length > 0 ? client.emails : [{ type: "personal", email: "", is_primary: true }],
      fax: client.fax || "",
      addresses: client.addresses?.length > 0 ? client.addresses : [{ type: "primary", street: "", city: "", province: "", postal_code: "", country: "Canada" }],
      company_id: client.company_id || "",
      birthday: client.birthday || "",
      anniversary: client.anniversary || "",
      preferred_contact_method: client.preferred_contact_method || "email",
      preferred_contact_time: client.preferred_contact_time || "",
      payment_terms: client.payment_terms || "Net 30",
      credit_limit: client.credit_limit || "",
      tax_exempt: client.tax_exempt || false,
      banking_info: client.banking_info || {
        bank_name: "",
        account_holder: "",
        account_number: "",
        transit_number: "",
        institution_number: ""
      },
      allergies: client.allergies || "",
      preferences: client.preferences || "",
      special_requirements: client.special_requirements || "",
      tags: client.tags || [],
      category: client.category || "",
      sub_contacts: client.sub_contacts || [],
      notes: client.notes || "",
      custom_fields: client.custom_fields || {},
    });
    setEditMode(true);
    setEditId(client.id);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!clientForm.first_name || !clientForm.last_name) {
      toast.error("First and last name are required");
      return;
    }

    setSaving(true);
    try {
      // Extract primary email for backend (which expects single `email` field).
      const primaryEmail =
        clientForm.emails?.find((e) => e.is_primary)?.email ||
        clientForm.emails?.[0]?.email ||
        "";
      const data = {
        ...clientForm,
        email: primaryEmail,
        credit_limit: clientForm.credit_limit ? parseFloat(clientForm.credit_limit) : null,
        company_id: clientForm.company_id || null,
      };

      if (editMode) {
        await axios.put(`${API}/clients/${editId}`, data);
        toast.success("Client updated");
      } else {
        await axios.post(`${API}/clients`, data);
        toast.success("Client created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm("Are you sure you want to archive this client?")) return;
    
    try {
      await axios.delete(`${API}/clients/${clientId}`);
      toast.success("Client archived");
      fetchClients();
      fetchStats();
    } catch (error) {
      toast.error("Failed to archive client");
    }
  };

  const addPhone = () => {
    setClientForm({
      ...clientForm,
      phones: [...clientForm.phones, { type: "cell", number: "", is_primary: false }],
    });
  };

  const removePhone = (index) => {
    const phones = clientForm.phones.filter((_, i) => i !== index);
    setClientForm({ ...clientForm, phones });
  };

  const addEmail = () => {
    setClientForm({
      ...clientForm,
      emails: [...clientForm.emails, { type: "personal", email: "", is_primary: false }],
    });
  };

  const removeEmail = (index) => {
    const emails = clientForm.emails.filter((_, i) => i !== index);
    setClientForm({ ...clientForm, emails });
  };

  const addTag = () => {
    if (tagInput && !clientForm.tags.includes(tagInput)) {
      setClientForm({ ...clientForm, tags: [...clientForm.tags, tagInput] });
      setTagInput("");
    }
  };

  const removeTag = (tag) => {
    setClientForm({ ...clientForm, tags: clientForm.tags.filter((t) => t !== tag) });
  };

  const getPrimaryEmail = (client) => {
    const primary = client.emails?.find((e) => e.is_primary);
    return primary?.email || client.emails?.[0]?.email || "-";
  };

  const getPrimaryPhone = (client) => {
    const primary = client.phones?.find((p) => p.is_primary);
    return primary?.number || client.phones?.[0]?.number || "-";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
            <User className="h-8 w-8 text-primary" />
            Clients
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage individual clients and their information
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(isOfficeManager || isQuoter) && (
            <>
              <Button
                variant="outline"
                onClick={() => setBulkInviteOpen(true)}
                data-testid="send-invites-btn"
                className="flex-1 sm:flex-initial"
              >
                <Mail className="h-4 w-4 mr-2" />
                Send Invites
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/bulk-upload?kind=client')}
                data-testid="bulk-add-clients-btn"
                className="flex-1 sm:flex-initial"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Bulk Add
              </Button>
              <Button
                onClick={() => { resetForm(); setDialogOpen(true); }}
                data-testid="add-client-btn"
                className="flex-1 sm:flex-initial"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Clients</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <User className="h-8 w-8 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">With Company</p>
                <p className="text-2xl font-bold">{stats.with_company}</p>
              </div>
              <Building2 className="h-8 w-8 text-violet-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Independent</p>
                <p className="text-2xl font-bold">{stats.independent}</p>
              </div>
              <User className="h-8 w-8 text-emerald-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search clients..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterCompany} onValueChange={setFilterCompany}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="All Companies" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Companies</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("list")}
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("grid")}
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client List/Grid */}
      {clients.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <User className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium">No clients found</h3>
            <p className="text-muted-foreground mt-1">
              {search || filterCompany ? "Try adjusting your filters" : "Add your first client to get started"}
            </p>
            {!search && !filterCompany && (isOfficeManager || isQuoter) && (
              <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add First Client
              </Button>
            )}
          </CardContent>
        </Card>
      ) : viewMode === "list" ? (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead className="hidden sm:table-cell">Email</TableHead>
                  <TableHead className="hidden md:table-cell">Phone</TableHead>
                  <TableHead className="hidden lg:table-cell">Company</TableHead>
                  <TableHead className="hidden md:table-cell">Portal</TableHead>
                  <TableHead className="hidden lg:table-cell">Tags</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell>
                      <Link to={`/clients/${client.id}`} className="flex items-center gap-3 hover:opacity-80 transition-opacity">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium text-primary hover:underline">{client.name}</p>
                          {client.nickname && (
                            <p className="text-xs text-muted-foreground">&quot;{client.nickname}&quot;</p>
                          )}
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <a href={`mailto:${getPrimaryEmail(client)}`} className="text-primary hover:underline">
                        {getPrimaryEmail(client)}
                      </a>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{getPrimaryPhone(client)}</TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {client.company_name ? (
                        <Badge variant="outline">{client.company_name}</Badge>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <PortalStatusBadge
                        portalStatus={client.portal_status}
                        targetType="client"
                        targetId={client.id}
                        targetEmail={getPrimaryEmail(client)}
                        onChanged={fetchClients}
                      />
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {client.tags?.slice(0, 2).map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                        {client.tags?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">+{client.tags.length - 2}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" asChild>
                          <Link to={`/clients/${client.id}`} data-testid={`view-client-${client.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(client)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        {isOfficeManager && (
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(client.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {clients.map((client) => (
            <Card key={client.id} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{client.name}</p>
                      {client.nickname && (
                        <p className="text-xs text-muted-foreground">&quot;{client.nickname}&quot;</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{getPrimaryEmail(client)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span>{getPrimaryPhone(client)}</span>
                  </div>
                  {client.company_name && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Building2 className="h-4 w-4" />
                      <span className="truncate">{client.company_name}</span>
                    </div>
                  )}
                </div>
                {client.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {client.tags.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t">
                  <PortalStatusBadge
                    portalStatus={client.portal_status}
                    targetType="client"
                    targetId={client.id}
                    targetEmail={getPrimaryEmail(client)}
                    onChanged={fetchClients}
                  />
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(client)}>
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl p-0">
          <DialogHeader className="flex-shrink-0 p-6 pb-2">
            <DialogTitle>{editMode ? "Edit Client" : "Add Client"}</DialogTitle>
            <DialogDescription>Enter client information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="flex-1 overflow-y-auto px-6 max-h-[60vh]">
              <Tabs defaultValue="basic" className="w-full">
                <TabsList className="w-full grid grid-cols-4">
                  <TabsTrigger value="basic">Basic</TabsTrigger>
                  <TabsTrigger value="contact">Contact</TabsTrigger>
                  <TabsTrigger value="personal">Personal</TabsTrigger>
                  <TabsTrigger value="billing">Billing</TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4 mt-4">
                  {/* Title and Names */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Title</Label>
                      <Select
                        value={clientForm.title || "none"}
                        onValueChange={(v) => setClientForm({ ...clientForm, title: v === "none" ? "" : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="Mr">Mr.</SelectItem>
                          <SelectItem value="Mrs">Mrs.</SelectItem>
                          <SelectItem value="Ms">Ms.</SelectItem>
                          <SelectItem value="Dr">Dr.</SelectItem>
                          <SelectItem value="Prof">Prof.</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>First Name *</Label>
                      <Input
                        value={clientForm.first_name}
                        onChange={(e) => setClientForm({ ...clientForm, first_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name *</Label>
                      <Input
                        value={clientForm.last_name}
                        onChange={(e) => setClientForm({ ...clientForm, last_name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Preferred Name</Label>
                      <Input
                        value={clientForm.preferred_name}
                        onChange={(e) => setClientForm({ ...clientForm, preferred_name: e.target.value })}
                        placeholder="What to call them"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Nickname</Label>
                      <Input
                        value={clientForm.nickname}
                        onChange={(e) => setClientForm({ ...clientForm, nickname: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Category</Label>
                      <CategoryManager
                        categoryType="client"
                        label="Client Type"
                        onCategoriesChange={() => {}}
                        showAsDropdown={true}
                        selectedValue={clientForm.category}
                        onSelectionChange={(v) => setClientForm({ ...clientForm, category: v })}
                        allowAll={false}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Associated Company</Label>
                    <Select
                      value={clientForm.company_id || "none"}
                      onValueChange={(v) => setClientForm({ ...clientForm, company_id: v === "none" ? "" : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select company (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Company</SelectItem>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <div className="flex gap-2">
                      <Input
                        value={tagInput}
                        onChange={(e) => setTagInput(e.target.value)}
                        placeholder="Add tag..."
                        onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                      />
                      <Button type="button" onClick={addTag}>Add</Button>
                    </div>
                    {clientForm.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {clientForm.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="gap-1">
                            {tag}
                            <X className="h-3 w-3 cursor-pointer" onClick={() => removeTag(tag)} />
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={clientForm.notes}
                      onChange={(e) => setClientForm({ ...clientForm, notes: e.target.value })}
                      rows={3}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="contact" className="space-y-4 mt-4">
                  {/* Phone Numbers */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Phone Numbers</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addPhone}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    {clientForm.phones.map((phone, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Select
                          value={phone.type}
                          onValueChange={(v) => {
                            const phones = [...clientForm.phones];
                            phones[index].type = v;
                            setClientForm({ ...clientForm, phones });
                          }}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cell">Cell</SelectItem>
                            <SelectItem value="work">Work</SelectItem>
                            <SelectItem value="home">Home</SelectItem>
                            <SelectItem value="fax">Fax</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          value={phone.number}
                          onChange={(e) => {
                            const phones = [...clientForm.phones];
                            phones[index].number = e.target.value;
                            setClientForm({ ...clientForm, phones });
                          }}
                          placeholder="Phone number"
                          className="flex-1"
                        />
                        <Input
                          value={phone.extension || ""}
                          onChange={(e) => {
                            const phones = [...clientForm.phones];
                            phones[index].extension = e.target.value;
                            setClientForm({ ...clientForm, phones });
                          }}
                          placeholder="Ext"
                          className="w-20"
                        />
                        {clientForm.phones.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(index)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Fax */}
                  <div className="space-y-2">
                    <Label>Fax Number</Label>
                    <Input
                      value={clientForm.fax}
                      onChange={(e) => setClientForm({ ...clientForm, fax: e.target.value })}
                      placeholder="Fax number"
                    />
                  </div>

                  {/* Email Addresses */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Email Addresses</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addEmail}>
                        <Plus className="h-4 w-4 mr-1" />
                        Add
                      </Button>
                    </div>
                    {clientForm.emails.map((email, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <Select
                          value={email.type}
                          onValueChange={(v) => {
                            const emails = [...clientForm.emails];
                            emails[index].type = v;
                            setClientForm({ ...clientForm, emails });
                          }}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="personal">Personal</SelectItem>
                            <SelectItem value="work">Work</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="email"
                          value={email.email}
                          onChange={(e) => {
                            const emails = [...clientForm.emails];
                            emails[index].email = e.target.value;
                            setClientForm({ ...clientForm, emails });
                          }}
                          placeholder="Email address"
                          className="flex-1"
                        />
                        {clientForm.emails.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeEmail(index)}>
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Preferred Contact */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Preferred Contact Method</Label>
                      <Select
                        value={clientForm.preferred_contact_method}
                        onValueChange={(v) => setClientForm({ ...clientForm, preferred_contact_method: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="text">Text Message</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Preferred Contact Time</Label>
                      <Input
                        value={clientForm.preferred_contact_time}
                        onChange={(e) => setClientForm({ ...clientForm, preferred_contact_time: e.target.value })}
                        placeholder="e.g., Mornings, After 5pm"
                      />
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="personal" className="space-y-4 mt-4">
                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Birthday</Label>
                      <Input
                        type="date"
                        value={clientForm.birthday}
                        onChange={(e) => setClientForm({ ...clientForm, birthday: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Anniversary</Label>
                      <Input
                        type="date"
                        value={clientForm.anniversary}
                        onChange={(e) => setClientForm({ ...clientForm, anniversary: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Personal Details */}
                  <div className="space-y-2">
                    <Label>Allergies</Label>
                    <Textarea
                      value={clientForm.allergies}
                      onChange={(e) => setClientForm({ ...clientForm, allergies: e.target.value })}
                      placeholder="Any known allergies..."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Preferences</Label>
                    <Textarea
                      value={clientForm.preferences}
                      onChange={(e) => setClientForm({ ...clientForm, preferences: e.target.value })}
                      placeholder="Communication preferences, scheduling preferences, etc."
                      rows={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Special Requirements</Label>
                    <Textarea
                      value={clientForm.special_requirements}
                      onChange={(e) => setClientForm({ ...clientForm, special_requirements: e.target.value })}
                      placeholder="Accessibility needs, special instructions, etc."
                      rows={2}
                    />
                  </div>
                </TabsContent>

                <TabsContent value="billing" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Terms</Label>
                      <Select
                        value={clientForm.payment_terms}
                        onValueChange={(v) => setClientForm({ ...clientForm, payment_terms: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Due on Receipt">Due on Receipt</SelectItem>
                          <SelectItem value="Net 15">Net 15</SelectItem>
                          <SelectItem value="Net 30">Net 30</SelectItem>
                          <SelectItem value="Net 45">Net 45</SelectItem>
                          <SelectItem value="Net 60">Net 60</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Credit Limit ($)</Label>
                      <Input
                        type="number"
                        value={clientForm.credit_limit}
                        onChange={(e) => setClientForm({ ...clientForm, credit_limit: e.target.value })}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="tax_exempt"
                      checked={clientForm.tax_exempt}
                      onChange={(e) => setClientForm({ ...clientForm, tax_exempt: e.target.checked })}
                      className="rounded"
                    />
                    <Label htmlFor="tax_exempt">Tax Exempt</Label>
                  </div>

                  {/* Banking Info */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium text-sm">Banking Information (Optional)</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Bank Name</Label>
                        <Input
                          value={clientForm.banking_info?.bank_name || ""}
                          onChange={(e) => setClientForm({ 
                            ...clientForm, 
                            banking_info: { ...clientForm.banking_info, bank_name: e.target.value }
                          })}
                          placeholder="e.g., TD Bank"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Account Holder</Label>
                        <Input
                          value={clientForm.banking_info?.account_holder || ""}
                          onChange={(e) => setClientForm({ 
                            ...clientForm, 
                            banking_info: { ...clientForm.banking_info, account_holder: e.target.value }
                          })}
                          placeholder="Name on account"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Institution #</Label>
                        <Input
                          value={clientForm.banking_info?.institution_number || ""}
                          onChange={(e) => setClientForm({ 
                            ...clientForm, 
                            banking_info: { ...clientForm.banking_info, institution_number: e.target.value }
                          })}
                          placeholder="3 digits"
                          maxLength={3}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Transit #</Label>
                        <Input
                          value={clientForm.banking_info?.transit_number || ""}
                          onChange={(e) => setClientForm({ 
                            ...clientForm, 
                            banking_info: { ...clientForm.banking_info, transit_number: e.target.value }
                          })}
                          placeholder="5 digits"
                          maxLength={5}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Account #</Label>
                        <Input
                          value={clientForm.banking_info?.account_number || ""}
                          onChange={(e) => setClientForm({ 
                            ...clientForm, 
                            banking_info: { ...clientForm.banking_info, account_number: e.target.value }
                          })}
                          placeholder="Account number"
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            {/* Phase 19.23 — Portal access toggle */}
            {!editMode && (
              <div className="px-6 pt-4 pb-2 border-t" data-testid="grant-portal-section">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="grant_portal_access"
                    checked={clientForm.grant_portal_access}
                    onCheckedChange={(checked) =>
                      setClientForm({
                        ...clientForm,
                        grant_portal_access: !!checked,
                        send_invite_now: checked ? autoSendInvitesDefault : null,
                      })
                    }
                    data-testid="grant-portal-checkbox"
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <Label htmlFor="grant_portal_access" className="cursor-pointer font-medium">
                      Grant portal access
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Creates a portal account so this client can view quotes, invoices, and message you.
                    </p>
                    {clientForm.grant_portal_access && (
                      <div className="flex items-center gap-2 mt-2">
                        <Checkbox
                          id="send_invite_now"
                          checked={!!clientForm.send_invite_now}
                          onCheckedChange={(checked) =>
                            setClientForm({ ...clientForm, send_invite_now: !!checked })
                          }
                          data-testid="send-invite-now-checkbox"
                        />
                        <Label htmlFor="send_invite_now" className="cursor-pointer text-sm">
                          Email the setup link now
                          {autoSendInvitesDefault && (
                            <span className="text-xs text-muted-foreground ml-1">(default ON)</span>
                          )}
                        </Label>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            <DialogFooter className="flex-shrink-0 border-t p-6 pt-4 bg-background">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editMode ? "Update Client" : "Add Client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Phase 19.23 — Bulk portal invite modal */}
      <BulkPortalInviteModal
        open={bulkInviteOpen}
        onClose={() => setBulkInviteOpen(false)}
        accountType="client_portal"
        onSent={() => fetchClients()}
      />
    </div>
  );
};

export default Clients;
